<template>
    <div class="modal-container modal-container--wide">
        <form @submit.prevent="handleSubmit" class="modal-form">
            <div class="modal-header">
                <div class="icon-container">
                    <PhMapPin :size="24" class="text-gray-500" />
                </div>
                <div class="header-text">
                    <h2>{{ isEditing ? 'Edit' : 'Add' }} Address</h2>
                    <p>{{ isEditing ? 'Update' : 'Add' }} an address for this employee</p>
                </div>
            </div>

            <div class="modal-body">
                <div class="form-row">
                    <label>Address Line 1</label>
                    <PrimeInputText v-model="formData.address_line_1" class="w-full" placeholder="Street address" />
                </div>

                <div class="form-row">
                    <label>Address Line 2</label>
                    <PrimeInputText v-model="formData.address_line_2" class="w-full" placeholder="Apt, suite, etc. (optional)" />
                </div>

                <div class="form-grid">
                    <div class="form-row">
                        <label>City</label>
                        <PrimeInputText v-model="formData.address_city" class="w-full" />
                    </div>
                    <div class="form-row">
                        <label>State/Region</label>
                        <PrimeInputText v-model="formData.address_region" class="w-full" />
                    </div>
                </div>

                <div class="form-grid">
                    <div class="form-row">
                        <label>Postal Code</label>
                        <PrimeInputText v-model="formData.address_postal_code" class="w-full" />
                    </div>
                    <div class="form-row">
                        <label>Country</label>
                        <PrimeInputText v-model="formData.address_country" class="w-full" placeholder="US" />
                    </div>
                </div>
            </div>

            <div class="modal-footer modal-footer--split">
                <PrimeButton
                    v-if="isEditing"
                    label="Delete"
                    severity="danger"
                    text
                    @click="handleDelete"
                    :disabled="isLoading"
                />
                <div class="flex-1" />
                <PrimeButton label="Cancel" @click="closeDialog" class="p-btn-root_secondary" :disabled="isLoading" />
                <PrimeButton label="Save" type="submit" :loading="isLoading" />
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import { PhMapPin } from '@phosphor-icons/vue';
import { businessService } from '~/services/business-service';
import { ModalNames } from '~/types/modals';
import { memberFacingMessage } from '~/utils/errors';

const dialogRef = getDialogRef(ModalNames.EDIT_EMPLOYEE_ADDRESS);
const { showSuccess, showError } = useGlobalToast();
const isLoading = ref(false);

const formData = reactive({
    id: undefined as number | undefined,
    address_line_1: '',
    address_line_2: '',
    address_city: '',
    address_region: '',
    address_postal_code: '',
    address_country: '',
});

const companyId = computed(() => dialogRef.value?.data?.companyId);
const employeeId = computed(() => dialogRef.value?.data?.employeeId);
const address = computed(() => dialogRef.value?.data?.address);
const isEditing = computed(() => !!formData.id);

onMounted(() => {
    if (address.value) {
        formData.id = address.value.id;
        formData.address_line_1 = address.value.line_1 || address.value.address_line_1 || '';
        formData.address_line_2 = address.value.line_2 || address.value.address_line_2 || '';
        formData.address_city = address.value.city || address.value.address_city || '';
        formData.address_region = address.value.region || address.value.address_region || '';
        formData.address_postal_code = address.value.postal_code || address.value.address_postal_code || '';
        formData.address_country = address.value.country || address.value.address_country || '';
    }
});

const closeDialog = () => {
    dialogRef.value.close();
};

const handleSubmit = async () => {
    isLoading.value = true;
    try {
        await businessService.saveEmployeeAddress(companyId.value, employeeId.value, formData);
        showSuccess('Success', `Address ${isEditing.value ? 'updated' : 'added'} successfully`);
        dialogRef.value.close({ updated: true });
    } catch (error) {
        showError('Error', memberFacingMessage(error, 'Failed to save address'));
    } finally {
        isLoading.value = false;
    }
};

const handleDelete = async () => {
    if (!formData.id) return;
    isLoading.value = true;
    try {
        await businessService.removeEmployeeAddress(companyId.value, employeeId.value, formData.id);
        showSuccess('Success', 'Address removed successfully');
        dialogRef.value.close({ updated: true });
    } catch (error) {
        showError('Error', memberFacingMessage(error, 'Failed to remove address'));
    } finally {
        isLoading.value = false;
    }
};
</script>

<style lang="scss" scoped>
@use '../../../assets/styles/business-form-modal';

.modal-body {
    .form-grid {
        @apply grid grid-cols-2 gap-4;
    }
}
</style>
